/**
 * Universe — unified parent page grouping Companies + Sources under a
 * single navigation surface.
 *
 * URL contract:
 *   /universe            → redirects to /universe/companies (in App.js)
 *   /universe/companies  → renders <IntelligenceBackbonePage hideHeader />
 *   /universe/sources    → renders <SourcesPage hideHeader />
 *
 * Legacy /companies and /sources routes are 302'd to the new URLs by
 * App.js — nothing else in the tree changes. The two child pages keep
 * their internal filters, tabs, state and data-testids intact.
 */
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";

import Companies from "@/pages/Companies";
import Sources from "@/pages/Sources";
import { Input } from "@/components/ui/input";
import { fetchers, qk } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "companies", to: "/universe/companies", label: "Companies" },
  { key: "sources",   to: "/universe/sources",   label: "Sources"   },
];

function useCounts() {
  // Reuse the same cached queries the child pages consume — no extra
  // network calls, no cache thrash. React Query dedupes automatically.
  const companiesQ = useQuery({
    queryKey: qk.companies(),
    queryFn: () => fetchers.companies({ limit: 1000 }),
  });
  const sourcesQ = useQuery({
    queryKey: qk.sources(),
    queryFn: fetchers.sources,
  });
  return {
    companies: companiesQ.data?.data?.length ?? null,
    sources: sourcesQ.data?.data?.length ?? null,
  };
}

export default function UniversePage() {
  const location = useLocation();
  const counts = useCounts();
  const [searchParams, setSearchParams] = useSearchParams();

  // The active tab is driven by the URL — the `tab` prop is passed from the
  // route element in App.js and always mirrors location.pathname. We derive
  // it from pathname anyway so back/forward + deep-links work identically.
  const effectiveTab = useMemo(
    () => (location.pathname.endsWith("/sources") ? "sources" : "companies"),
    [location.pathname],
  );

  // Shared search state — synced to `?q=` for deep-linking. Debounced URL
  // write so typing doesn't spam history entries.
  const initialQ = searchParams.get("q") || "";
  const [q, setQ] = useState(initialQ);
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (q) next.set("q", q); else next.delete("q");
      // Only push if actually changed — avoids infinite loops.
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const placeholder = effectiveTab === "sources"
    ? "Search source, company, url…"
    : "Search company, website…";

  return (
    <div data-testid="universe-page">
      {/* Header — kept minimal so the child tabs / filters remain the
          dominant surface below. */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-hm-fg tracking-tight">
              Universe
            </h1>
          </div>
          <p className="mt-2 text-sm text-hm-fg-muted max-w-2xl leading-relaxed">
            The complete Beyond Activ market intelligence graph — companies
            and sources unified.
          </p>
        </div>

        {/* Counters — right-aligned mono badges */}
        <div
          className="hidden sm:flex items-center gap-2 shrink-0"
          data-testid="universe-counters"
        >
          <span
            className="mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-hm-border bg-hm-bg-1 text-hm-fg-muted"
            data-testid="universe-count-companies"
          >
            {counts.companies ?? "…"} Companies
          </span>
          <span
            className="mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-hm-border bg-hm-bg-1 text-hm-fg-muted"
            data-testid="universe-count-sources"
          >
            {counts.sources ?? "…"} Sources
          </span>
        </div>
      </div>

      {/* Sticky tab bar — segmented control on the left, shared search on
          the right. URL-driven so deep-links (`?q=…`) survive reload. */}
      <div
        className="sticky top-0 z-10 -mx-6 px-6 pb-3 pt-1 mb-4 bg-hm-bg border-b border-hm-border"
        data-testid="universe-tab-bar"
      >
        <div className="flex items-center justify-between gap-4">
          <div
            className="inline-flex items-center gap-1 p-1 rounded-lg border border-hm-border bg-hm-bg-1"
            role="tablist"
          >
            {TABS.map((t) => {
              const active = t.key === effectiveTab;
              return (
                <NavLink
                  key={t.key}
                  to={{ pathname: t.to, search: location.search }}
                  data-testid={`universe-tab-${t.key}`}
                  role="tab"
                  aria-selected={active}
                  className={cn(
                    "text-sm font-medium px-4 py-1.5 rounded-md transition-all",
                    active
                      ? "bg-hm-bg text-hm-fg border border-hm-border shadow-sm"
                      : "text-hm-fg-muted hover:text-hm-fg",
                  )}
                >
                  {t.label}
                  <span
                    className="mono text-[11px] ml-1.5 text-hm-fg-subtle"
                    data-testid={`universe-tab-${t.key}-count`}
                  >
                    {t.key === "companies"
                      ? (counts.companies ?? "…")
                      : (counts.sources ?? "…")}
                  </span>
                </NavLink>
              );
            })}
          </div>

          {/* Shared search — right-aligned. Uses the same shadcn Input styling
              as the previous internal search bars for perfect design parity. */}
          <div className="relative w-[320px] shrink-0">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-hm-fg-subtle pointer-events-none"
              aria-hidden
            />
            <Input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              data-testid="universe-search"
              className="h-9 pl-9 pr-3 text-sm bg-hm-bg-1 border-hm-border text-hm-fg placeholder:text-hm-fg-subtle focus-visible:border-hm-accent-dim focus-visible:ring-hm-accent-dim"
            />
          </div>
        </div>
      </div>

      {/* Active tab content */}
      <div data-testid={`universe-panel-${effectiveTab}`}>
        {effectiveTab === "sources" ? (
          <Sources hideHeader externalQ={q} />
        ) : (
          <Companies hideHeader externalQ={q} />
        )}
      </div>
    </div>
  );
}
